import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, FileText, ShoppingCart, LogOut, Users, BarChart3, UserCog, Bell, Settings, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useCallback } from 'react';
import { notificationsAPI, authAPI } from '../utils/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';

const Layout = ({ user, setUser, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notifications as read');
    }
  };

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch { /* cookie might already be expired */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/projects', label: 'Projects', icon: FolderKanban },
    { path: '/sales-orders', label: 'Sales Orders', icon: FileText },
    { path: '/retainers', label: 'Retainers', icon: RefreshCw },
    ...(user?.role === 'accounts' || user?.role === 'admin' ? [
      { path: '/clients', label: 'Clients', icon: Users },
    ] : []),
    ...(user?.role === 'admin' ? [
      { path: '/users', label: 'Users', icon: UserCog },
    ] : []),
    ...(user?.role === 'accounts' || user?.role === 'admin' ? [
      { path: '/reports', label: 'Reports', icon: BarChart3 }
    ] : []),
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'admin': return 'Admin';
      case 'accounts': return 'Accounts';
      case 'signing_authority': return 'Signing Authority';
      case 'manager': return 'Manager';
      default: return user?.role || 'User';
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="w-64 bg-white border-r border-border flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img 
              src="/company-logo.png" 
              alt="Creative Khichdi P. Limited" 
              className="h-10 w-10 object-contain rounded-full"
              data-testid="sidebar-company-logo"
            />
            <div>
              <h1 className="text-lg font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Invoice Tracker
              </h1>
              <p className="text-xs text-muted-foreground">{getRoleLabel()}</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-border">
          <div className="mb-3 px-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold">Notifications</h4>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-96">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No notifications
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`p-3 rounded-md text-sm ${
                              notif.read ? 'bg-muted/50' : 'bg-blue-50 border border-blue-200'
                            }`}
                            data-testid={`notification-${notif.id}`}
                          >
                            <p className={notif.read ? 'text-muted-foreground' : 'font-medium'}>
                              {notif.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
